/**
 * Alpha/foreground refinement for background removal.
 *
 * WHY THIS EXISTS
 * ---------------
 * A segmentation model gives you alpha. Compositing `source RGB x alpha` onto
 * a new background is what almost every browser tool does, and it is wrong at
 * exactly the pixels people look at. A hair pixel that was 40% hair over a
 * green wall has the wall's green baked into its RGB; multiplying by alpha
 * keeps that green and you get a coloured fringe on the new background. The
 * darker/more saturated the original background, the uglier it is.
 *
 * The composite is  C = a*F + (1-a)*B. We observe C and estimate a, so F is
 * recoverable — that is what estimateForeground() does (multi-level method of
 * Germer et al., the same one pymatting ships as estimate_foreground_ml).
 * Every function here is pure and works on plain typed arrays so it can be
 * unit-tested in node without a canvas — see tests/scratch/refine-check.mjs.
 *
 * LOADING: this is a PLAIN script with no import/export, on purpose. The
 * inference worker is a classic worker (it needs importScripts for ort.min.js,
 * which is UMD), the tool page wants the same feather/shift maths for its live
 * edge sliders, and the node unit test wants it too. One file with a global
 * serves all three; an ES module would have forced either a second copy or a
 * dynamic import() the old-browser cohort we are trying to rescue may not have.
 *
 * Conventions: rgb is Float32Array(w*h*3) in 0..1, alpha is Float32Array(w*h)
 * in 0..1, row-major.
 */

(function (root) {
'use strict';

/** Bilinear resize of an interleaved float image with `ch` channels. */
function resizeBilinear(src, sw, sh, dw, dh, ch) {
  if (sw === dw && sh === dh) return src.slice();
  const out = new Float32Array(dw * dh * ch);
  const xr = sw / dw, yr = sh / dh;
  for (let y = 0; y < dh; y++) {
    // sample at pixel centres, clamped — avoids the half-pixel drift that
    // makes a matte creep by one pixel per pyramid level
    let sy = (y + 0.5) * yr - 0.5;
    if (sy < 0) sy = 0; else if (sy > sh - 1) sy = sh - 1;
    const y0 = sy | 0, y1 = Math.min(y0 + 1, sh - 1), fy = sy - y0;
    for (let x = 0; x < dw; x++) {
      let sx = (x + 0.5) * xr - 0.5;
      if (sx < 0) sx = 0; else if (sx > sw - 1) sx = sw - 1;
      const x0 = sx | 0, x1 = Math.min(x0 + 1, sw - 1), fx = sx - x0;
      const i00 = (y0 * sw + x0) * ch, i01 = (y0 * sw + x1) * ch;
      const i10 = (y1 * sw + x0) * ch, i11 = (y1 * sw + x1) * ch;
      const o = (y * dw + x) * ch;
      for (let c = 0; c < ch; c++) {
        const top = src[i00 + c] + (src[i01 + c] - src[i00 + c]) * fx;
        const bot = src[i10 + c] + (src[i11 + c] - src[i10 + c]) * fx;
        out[o + c] = top + (bot - top) * fy;
      }
    }
  }
  return out;
}

/** Pyramid of (w,h) from smallest (<= smallSize) up to the full size. */
function pyramidSizes(w, h, smallSize) {
  const sizes = [[w, h]];
  let cw = w, ch = h;
  while (Math.min(cw, ch) > smallSize) {
    cw = Math.max(1, cw >> 1);
    ch = Math.max(1, ch >> 1);
    sizes.push([cw, ch]);
  }
  return sizes.reverse();
}

/**
 * Recover the true foreground colour behind a soft alpha.
 *
 * Returns Float32Array(w*h*3). Cost is O(pixels) with a small constant; the
 * pyramid is what keeps it from needing a global solve.
 */
function estimateForeground(rgb, alpha, w, h, opts) {
  const o = opts || {};
  const regularization = o.regularization != null ? o.regularization : 1e-5;
  const nSmall = o.smallIterations != null ? o.smallIterations : 10;
  const nBig = o.bigIterations != null ? o.bigIterations : 2;
  const smallSize = o.smallSize != null ? o.smallSize : 32;

  const sizes = pyramidSizes(w, h, smallSize);
  const [w0, h0] = sizes[0];

  let F = resizeBilinear(rgb, w, h, w0, h0, 3);
  let B = F.slice();

  for (let lvl = 0; lvl < sizes.length; lvl++) {
    const [lw, lh] = sizes[lvl];
    if (lvl > 0) {
      const [pw, ph] = sizes[lvl - 1];
      F = resizeBilinear(F, pw, ph, lw, lh, 3);
      B = resizeBilinear(B, pw, ph, lw, lh, 3);
    }
    const I = resizeBilinear(rgb, w, h, lw, lh, 3);
    const A = resizeBilinear(alpha, w, h, lw, lh, 1);
    const iters = lvl === sizes.length - 1 ? nBig : nSmall;

    // Neighbour offsets are hoisted and the 4-neighbourhood unrolled: this
    // loop runs tens of millions of times on a 12 MP photo, and the naive
    // k-loop with per-neighbour bounds checks measured ~4x slower.
    for (let it = 0; it < iters; it++) {
      for (let y = 0; y < lh; y++) {
        const rowUp = y > 0 ? -lw : 0;
        const rowDn = y < lh - 1 ? lw : 0;
        for (let x = 0; x < lw; x++) {
          const p = y * lw + x, p3 = p * 3;
          const a0 = A[p], a1 = 1 - a0;
          let a00 = a0 * a0, a11 = a1 * a1;
          const a01 = a0 * a1;
          let b0r = a0 * I[p3], b0g = a0 * I[p3 + 1], b0b = a0 * I[p3 + 2];
          let b1r = a1 * I[p3], b1g = a1 * I[p3 + 1], b1b = a1 * I[p3 + 2];

          // Weighted by how much alpha DISAGREES with the neighbour: at the
          // boundary that pull is strong, which is what lets a hair pixel
          // borrow its colour from solid hair rather than from the wall.
          const nL = x > 0 ? p - 1 : -1;
          const nR = x < lw - 1 ? p + 1 : -1;
          const nU = rowUp ? p + rowUp : -1;
          const nD = rowDn ? p + rowDn : -1;
          for (let k = 0; k < 4; k++) {
            const q = k === 0 ? nL : k === 1 ? nR : k === 2 ? nU : nD;
            if (q < 0) continue;
            const q3 = q * 3;
            const d = a0 - A[q];
            const da = regularization + (d < 0 ? -d : d);
            a00 += da; a11 += da;
            b0r += da * F[q3]; b0g += da * F[q3 + 1]; b0b += da * F[q3 + 2];
            b1r += da * B[q3]; b1g += da * B[q3 + 1]; b1b += da * B[q3 + 2];
          }

          const det = a00 * a11 - a01 * a01;
          const inv = det > 1e-12 ? 1 / det : 0;
          F[p3]     = clamp01((a11 * b0r - a01 * b1r) * inv);
          F[p3 + 1] = clamp01((a11 * b0g - a01 * b1g) * inv);
          F[p3 + 2] = clamp01((a11 * b0b - a01 * b1b) * inv);
          B[p3]     = clamp01((a00 * b1r - a01 * b0r) * inv);
          B[p3 + 1] = clamp01((a00 * b1g - a01 * b0g) * inv);
          B[p3 + 2] = clamp01((a00 * b1b - a01 * b0b) * inv);
        }
      }
    }
  }
  return F;
}

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

/**
 * Separable box blur repeated 3x — a fast, good-enough Gaussian for feathering
 * an alpha channel. radius is in pixels; 0 is a no-op.
 */
function featherAlpha(alpha, w, h, radius) {
  const r = Math.round(radius);
  if (r <= 0) return alpha.slice();
  let cur = alpha.slice();
  const tmp = new Float32Array(w * h);
  for (let pass = 0; pass < 3; pass++) {
    boxH(cur, tmp, w, h, r);
    boxV(tmp, cur, w, h, r);
  }
  return cur;
}

function boxH(src, dst, w, h, r) {
  const norm = 1 / (2 * r + 1);
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let acc = 0;
    for (let i = -r; i <= r; i++) acc += src[row + clampi(i, 0, w - 1)];
    for (let x = 0; x < w; x++) {
      dst[row + x] = acc * norm;
      acc -= src[row + clampi(x - r, 0, w - 1)];
      acc += src[row + clampi(x + r + 1, 0, w - 1)];
    }
  }
}

function boxV(src, dst, w, h, r) {
  const norm = 1 / (2 * r + 1);
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let i = -r; i <= r; i++) acc += src[clampi(i, 0, h - 1) * w + x];
    for (let y = 0; y < h; y++) {
      dst[y * w + x] = acc * norm;
      acc -= src[clampi(y - r, 0, h - 1) * w + x];
      acc += src[clampi(y + r + 1, 0, h - 1) * w + x];
    }
  }
}

function clampi(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

/**
 * Move the matte edge in (negative) or out (positive) by `amount` pixels.
 *
 * Implemented as a threshold shift on a blurred copy rather than a
 * morphological op: erode/dilate on a SOFT alpha quantises it to 0/1 and
 * destroys exactly the semi-transparency that hair lives in. Shifting the
 * decision level keeps the gradient and just slides it.
 */
function shiftEdge(alpha, w, h, amount) {
  const a = Math.round(amount);
  if (a === 0) return alpha.slice();
  const r = Math.abs(a);
  const grow = a > 0;
  // Greyscale morphology, separable. Unlike BINARY erode/dilate it keeps the
  // soft ramp intact and simply slides it by exactly r pixels — which is what
  // "expand the cutout by 2px" should mean. Blur-and-rethreshold, the obvious
  // alternative, both moves AND smears the edge.
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  morphH(alpha, tmp, w, h, r, grow);
  morphV(tmp, out, w, h, r, grow);
  return out;
}

function morphH(src, dst, w, h, r, grow) {
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let best = src[row + x];
      const lo = Math.max(0, x - r), hi = Math.min(w - 1, x + r);
      for (let i = lo; i <= hi; i++) {
        const v = src[row + i];
        if (grow ? v > best : v < best) best = v;
      }
      dst[row + x] = best;
    }
  }
}

function morphV(src, dst, w, h, r, grow) {
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      let best = src[y * w + x];
      const lo = Math.max(0, y - r), hi = Math.min(h - 1, y + r);
      for (let i = lo; i <= hi; i++) {
        const v = src[i * w + x];
        if (grow ? v > best : v < best) best = v;
      }
      dst[y * w + x] = best;
    }
  }
}

/**
 * Push alpha away from the middle. A segmentation model hedges: it leaves a
 * band of 0.3..0.7 around every edge, which reads as a soft halo on a
 * contrasting background. Raising contrast around 0.5 hardens the decision
 * while leaving genuine semi-transparency (hair, glass) intact if `strength`
 * is modest.
 */
function sharpenAlpha(alpha, strength) {
  const s = strength || 0;
  if (s <= 0) return alpha.slice();
  const k = 1 + s * 6;
  const out = new Float32Array(alpha.length);
  for (let i = 0; i < alpha.length; i++) {
    const v = alpha[i];
    out[i] = v <= 0 ? 0 : v >= 1 ? 1 : clamp01(0.5 + (v - 0.5) * k);
  }
  return out;
}

/**
 * Compose the final RGBA using estimated foreground colours.
 *
 * `bg` may be:
 *   null                        — transparent output (straight alpha)
 *   [r,g,b] in 0..1             — solid colour, no plate allocated
 *   Float32Array(w*h*3)         — full background plate
 */
function composite(F, alpha, w, h, bg) {
  const out = new Uint8ClampedArray(w * h * 4);
  const solid = Array.isArray(bg) || (bg && bg.length === 3 && w * h !== 1);
  for (let p = 0, p3 = 0, p4 = 0; p < w * h; p++, p3 += 3, p4 += 4) {
    const a = alpha[p];
    if (!bg) {
      out[p4] = F[p3] * 255; out[p4 + 1] = F[p3 + 1] * 255; out[p4 + 2] = F[p3 + 2] * 255;
      out[p4 + 3] = a * 255;
    } else {
      const br = solid ? bg[0] : bg[p3];
      const bgr = solid ? bg[1] : bg[p3 + 1];
      const bb = solid ? bg[2] : bg[p3 + 2];
      out[p4]     = (F[p3]     * a + br  * (1 - a)) * 255;
      out[p4 + 1] = (F[p3 + 1] * a + bgr * (1 - a)) * 255;
      out[p4 + 2] = (F[p3 + 2] * a + bb  * (1 - a)) * 255;
      out[p4 + 3] = 255;
    }
  }
  return out;
}

root.BgRefine = { resizeBilinear, estimateForeground, featherAlpha, shiftEdge, sharpenAlpha, composite };
})(typeof globalThis !== 'undefined' ? globalThis : self);
